import type { SnapshotFrom } from 'xstate'
import type multiCartMachine from './multiCartMachine'
import type { VisibleUserCartMultiCartSummary } from 'lib/userCart/multiCartVisibility'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { filterVisibleUserCartMultiCarts, isDefaultMultiCart, isVisibleUserCartMultiCart } from 'lib/userCart/multiCartVisibility'

export type MultiCartSnapshot = SnapshotFrom<typeof multiCartMachine>

const EmptyMultiCartItemMap: Record<string, App.MultiCartItem> = EmptyObject

export const selectItemsByActiveCart = (snapshot: MultiCartSnapshot): Record<string, App.MultiCartItem> => {
  const { cartItems, activeCartId } = snapshot.context
  if (cartItems.status !== 'loaded' || !activeCartId) return EmptyMultiCartItemMap
  return cartItems.data[activeCartId] ?? EmptyMultiCartItemMap
}

export const selectItemsInActiveCart = (snapshot: MultiCartSnapshot): Array<App.MultiCartItem> => {
  const activeCartItems = selectItemsByActiveCart(snapshot)
  if (activeCartItems === EmptyMultiCartItemMap) return EmptyArray
  return Object.values(activeCartItems)
}

export const selectVisibleCartSummaries = (snapshot: MultiCartSnapshot): Array<VisibleUserCartMultiCartSummary> => {
  const { cartSummary } = snapshot.context
  if (cartSummary.status !== 'loaded') return EmptyArray
  return filterVisibleUserCartMultiCarts(Object.values(cartSummary.data))
}

export const selectActiveVisibleCart = (snapshot: MultiCartSnapshot): VisibleUserCartMultiCartSummary | undefined => {
  const { activeCartId, cartSummary } = snapshot.context
  if (cartSummary.status !== 'loaded' || !activeCartId) return undefined

  const activeCart = cartSummary.data[activeCartId]
  return isVisibleUserCartMultiCart(activeCart) ? activeCart : undefined
}

export const selectEffectiveVisibleCart = (snapshot: MultiCartSnapshot): VisibleUserCartMultiCartSummary | undefined => {
  const activeVisibleCart = selectActiveVisibleCart(snapshot)
  if (activeVisibleCart) return activeVisibleCart

  const visibleCartSummaries = selectVisibleCartSummaries(snapshot)
  return visibleCartSummaries.find(isDefaultMultiCart) ?? visibleCartSummaries[0]
}

export const selectItemsByActiveVisibleCart = (snapshot: MultiCartSnapshot): Record<string, App.MultiCartItem> => {
  const { cartItems } = snapshot.context
  const activeVisibleCart = selectActiveVisibleCart(snapshot)
  if (cartItems.status !== 'loaded' || !activeVisibleCart) return EmptyMultiCartItemMap

  return cartItems.data[activeVisibleCart.cartId] ?? EmptyMultiCartItemMap
}

export const selectItemsByEffectiveVisibleCart = (snapshot: MultiCartSnapshot): Record<string, App.MultiCartItem> => {
  const { cartItems } = snapshot.context
  const effectiveVisibleCart = selectEffectiveVisibleCart(snapshot)
  if (cartItems.status !== 'loaded' || !effectiveVisibleCart) return EmptyMultiCartItemMap

  return cartItems.data[effectiveVisibleCart.cartId] ?? EmptyMultiCartItemMap
}

export const selectItemsInActiveVisibleCart = (snapshot: MultiCartSnapshot): Array<App.MultiCartItem> => {
  const activeVisibleCartItems = selectItemsByActiveVisibleCart(snapshot)
  if (activeVisibleCartItems === EmptyMultiCartItemMap) return EmptyArray
  return Object.values(activeVisibleCartItems)
}

export const selectItemsInEffectiveVisibleCart = (snapshot: MultiCartSnapshot): Array<App.MultiCartItem> => {
  const effectiveVisibleCartItems = selectItemsByEffectiveVisibleCart(snapshot)
  if (effectiveVisibleCartItems === EmptyMultiCartItemMap) return EmptyArray
  return Object.values(effectiveVisibleCartItems)
}
